import type { Icon } from "@phosphor-icons/react";
import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { Tooltip } from "~/foundation/components/Tooltip";
import { cn } from "~/foundation/utils/cn";

export interface SidebarLinkProps {
  to: string;
  icon: Icon;
  label: string;
  active: boolean;
  disabled?: boolean;
  /** Rail mode: icon only, label promoted to a tooltip on the inline-end side. */
  collapsed?: boolean;
  onNavigate?: () => void;
  /**
   * A control that rides at the inline-end of the row (the pin star), revealed on hover or
   * focus. Rendered as a sibling of the link, never inside it — a button inside an anchor is
   * invalid HTML and steals the click.
   */
  action?: ReactNode;
}

export function SidebarLink({
  to,
  icon: IconComponent,
  label,
  active,
  disabled,
  collapsed,
  onNavigate,
  action,
}: SidebarLinkProps) {
  const withAction = !collapsed && action !== undefined;
  const className = cn(
    "flex h-8 min-w-0 items-center rounded-sm text-body font-medium text-sidebar-text",
    "transition-colors duration-100 ease-in-out motion-reduce:transition-none",
    collapsed ? "w-8 justify-center" : "gap-2.5 px-2",
    withAction && "flex-1 pe-8",
    active ? "bg-sidebar-active-bg text-text" : "hover:bg-hover-bg",
    disabled && "pointer-events-none opacity-40",
  );

  const content = (
    <>
      <IconComponent size={20} aria-hidden className="shrink-0" />
      {collapsed ? null : <span className="truncate">{label}</span>}
    </>
  );

  const link = disabled ? (
    <span className={className} aria-disabled="true" title={label}>
      {content}
    </span>
  ) : (
    <Link
      // The nav registry stores plain paths; the router's typed `to` is recovered by
      // routes.tsx, which is the file that owns which paths exist.
      to={to as never}
      className={className}
      onClick={onNavigate}
      aria-current={active ? "page" : undefined}
      aria-label={collapsed ? label : undefined}
    >
      {content}
    </Link>
  );

  if (withAction) {
    return (
      <div className="group relative flex items-center">
        {link}
        <span className="absolute end-1 top-1/2 -translate-y-1/2 opacity-0 transition-opacity duration-100 group-hover:opacity-100 group-focus-within:opacity-100 has-[[data-pinned=true]]:opacity-100 motion-reduce:transition-none">
          {action}
        </span>
      </div>
    );
  }

  if (!collapsed) {
    return link;
  }

  return (
    <Tooltip content={label} placement="inline-end">
      {link}
    </Tooltip>
  );
}
